import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as D from '@/components/ui/dialog';

// ui/dialog.tsx is untyped (forwardRef without props): loose types, as in ConfirmDialog.
type Loose = ComponentType<Record<string, unknown> & { children?: ReactNode }>;
const Dialog = D.Dialog as unknown as Loose;
const DialogContent = D.DialogContent as unknown as Loose;
const DialogHeader = D.DialogHeader as unknown as Loose;
const DialogTitle = D.DialogTitle as unknown as Loose;
const DialogDescription = D.DialogDescription as unknown as Loose;

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
const MOD = isMac ? '⌘' : 'Ctrl';

/**
 * The dialer's keyboard help, opened by `?` or from ⌘K (CEO-4). It lists the
 * shortcuts that exist today; the call page adds its own in W3 (spec §4.6).
 */
export const DIALER_SHORTCUTS: Array<{ keys: string[]; labelKey: string }> = [
  { keys: [MOD, 'K'], labelKey: 'palette' },
  { keys: ['/'], labelKey: 'searchList' },
  { keys: [MOD, 'B'], labelKey: 'toggleMenu' },
  { keys: ['?'], labelKey: 'help' },
  { keys: ['Échap'], labelKey: 'close' },
];

export function ShortcutsHelpDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation(['common']);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('common:shortcuts.title')}</DialogTitle>
          <DialogDescription>{t('common:shortcuts.description')}</DialogDescription>
        </DialogHeader>
        <dl className="flex flex-col" style={{ gap: 2 }}>
          {DIALER_SHORTCUTS.map((s) => (
            <div key={s.labelKey} className="flex items-center justify-between rounded-md px-2 py-2 text-[13px]" style={{ gap: 12 }}>
              <dt style={{ color: 'var(--vf-text-1)' }}>{t(`common:shortcuts.items.${s.labelKey}`)}</dt>
              <dd className="flex shrink-0" style={{ gap: 4, margin: 0 }}>
                {s.keys.map((k) => (
                  <kbd key={k} className="vf-font-mono rounded border px-1.5 text-[12px]" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-2)', color: 'var(--vf-text-2)', minWidth: 22, textAlign: 'center' }}>
                    {/* i18n-exempt: a key identifier compared; its label comes from t() */}
                    {k === 'Échap' ? t('common:shortcuts.esc') : k}
                  </kbd>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  );
}

export default ShortcutsHelpDialog;
